import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];

type Example = {
  subject: string;
  task: string;
  hints: { label: string; text: string }[];
};

const examples: Example[] = [
  {
    subject: 'General Mathematics 3',
    task: 'Solve (x − 3)(2x + 1) = 0',
    hints: [
      { label: 'Gentle nudge', text: 'What do you notice about the two factors? Could either one be zero?' },
      { label: 'Bigger hint', text: 'If a × b = 0, then a = 0 or b = 0. Try setting each bracket equal to zero.' },
      { label: 'Step by step', text: 'x − 3 = 0 gives x = 3, and 2x + 1 = 0 gives x = −½. Now check both in the original equation.' },
    ],
  },
  {
    subject: 'English 3',
    task: 'Analyse how the poet presents loss in the final stanza',
    hints: [
      { label: 'Gentle nudge', text: 'Look closely at the imagery in those last lines — what feeling does it create for the reader?' },
      { label: 'Bigger hint', text: 'Pick one technique, like a metaphor or repetition, and quote the exact words from the stanza.' },
      { label: 'Step by step', text: 'Start with the technique ("Through the metaphor of…"), link it to the sense of loss, then explain the effect on the reader.' },
    ],
  },
  {
    subject: 'Modern History 3',
    task: 'How useful is Source A for understanding soldiers\' experiences at Gallipoli?',
    hints: [
      { label: 'Gentle nudge', text: 'What does the source actually show? Who wrote it, and when?' },
      { label: 'Bigger hint', text: 'Now think about what\'s missing — whose voices aren\'t in it, and what might they have said?' },
      { label: 'Step by step', text: 'State the source\'s origin, give one strength and one limitation, then judge its overall usefulness for the question.' },
    ],
  },
  {
    subject: 'Economics 3',
    task: 'Explain how an increase in the cash rate affects household spending',
    hints: [
      { label: 'Gentle nudge', text: 'Think about borrowing costs — what happens to a mortgage repayment when the rate rises?' },
      { label: 'Bigger hint', text: 'Connect that to disposable income, then to what households choose to spend and save.' },
      { label: 'Step by step', text: 'Higher cash rate → dearer loans → less disposable income → less spending. Add a real example, then mention the flip side for savers.' },
    ],
  },
];

const ROTATE_MS = 9000;

export default function HintLadderDemo({ className = '' }: { className?: string }) {
  const [exampleIndex, setExampleIndex] = useState(0);
  const [step, setStep] = useState(0);
  const example = examples[exampleIndex];

  useEffect(() => {
    const timer = setInterval(() => {
      setExampleIndex((i) => (i + 1) % examples.length);
      setStep(0);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={`glass-card rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl p-5 shadow-2xl text-left ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <AnimatePresence mode="wait">
          <motion.span
            key={example.subject}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease }}
            className="text-[11px] font-semibold uppercase tracking-[0.15em] text-accent"
          >
            {example.subject}
          </motion.span>
        </AnimatePresence>
        <span className="text-[11px] text-muted-foreground">Try it — no sign-up</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={example.task}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease }}
          className="font-display font-semibold text-foreground mb-4"
        >
          {example.task}
        </motion.p>
      </AnimatePresence>

      <div className="space-y-2 min-h-[4rem]">
        <AnimatePresence initial={false}>
          {example.hints.slice(0, step).map((h) => (
            <motion.div
              key={h.label}
              initial={{ opacity: 0, y: 8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.45, ease }}
              className="rounded-xl bg-accent/10 border border-accent/20 px-3 py-2"
            >
              <p className="text-[11px] font-semibold text-accent mb-0.5">Dev · {h.label}</p>
              <p className="text-sm text-foreground/90 leading-snug">{h.text}</p>
            </motion.div>
          ))}
        </AnimatePresence>
        {step === 0 && (
          <p className="text-sm text-muted-foreground">Stuck? Dev never just hands over the answer — see how the hints build up.</p>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        {step < example.hints.length ? (
          <Button size="sm" variant="accent" className="rounded-full min-h-[44px] px-5" onClick={() => setStep((s) => s + 1)}>
            <Lightbulb className="w-4 h-4 mr-1.5" />
            {step === 0 ? 'Ask Dev for a hint' : 'Another hint'}
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="rounded-full min-h-[44px] px-5" onClick={() => setStep(0)}>
            <RotateCcw className="w-4 h-4 mr-1.5" /> Start again
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1" aria-label={`Hint ${step} of ${example.hints.length}`}>
          {example.hints.map((_, i) => (
            <span key={i} className={`h-1.5 w-5 rounded-full transition-colors ${i < step ? 'bg-accent' : 'bg-border'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
